import { get, isEmpty } from 'lodash';
import { RSAA, RSAAAction, RSAACall } from 'redux-api-middleware';
import { signOut } from 'store/auth/authActions';
import { getMsalInstance } from 'store/auth/msalConfig';
import { resetAuthTokens } from 'utilities/authTokens';

// Lazy store access to avoid circular dependency:
// createAPIAction -> createStore -> reducers -> reducer -> actions -> createAPIAction
const getStore = () => require('store/createStore').store; // eslint-disable-line

/**
 * Handles 401 by clearing auth state and redirecting to MSAL login.
 * Uses logoutRedirect if MSAL has accounts (properly clears MSAL cache),
 * otherwise redirects directly to /auth.
 */
const handleUnauthorized = async (redirectPath: string) => {
  // Clear app tokens and Redux state
  resetAuthTokens();
  getStore().dispatch(signOut());

  const instance = getMsalInstance();
  if (instance?.getAllAccounts()?.length) {
    // Use MSAL's logoutRedirect to properly clear MSAL cache
    const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
    try {
      await instance.logoutRedirect({
        account,
        postLogoutRedirectUri: `${window.location.origin}/auth?redirect=${redirectPath}`,
      });
    } catch {
      // If logoutRedirect fails, fall back to direct navigation
      window.location.href = `/auth?redirect=${redirectPath}`;
    }
  } else {
    window.location.href = `/auth?redirect=${redirectPath}`;
  }
};

// Inline selector to avoid circular dependency:
// createAPIAction -> selectors -> reducers -> reducer -> actions -> createAPIAction
const selectAccessTokenFromState = (state: any): string | undefined =>
  get(state, 'auth.tokens.accessToken');

const baseUrl = '/api';

/**
 * returns array of action names to use them as default types for RSAA
 */
export const getActionTypes = (
  actionName: string
): [string, string, string] => [
  `${actionName}/REQUEST`,
  `${actionName}/SUCCESS`,
  `${actionName}/FAIL`,
];

const convertParamsToUrl = (params: any) => {
  const urlParams = new URLSearchParams(params);
  return !isEmpty(params) ? `?${urlParams.toString()}` : '';
};

const getAccessHeader = (token?: string, isFormData = false) => {
  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  if (!isFormData) {
    headers.Accept = 'application/json';
    headers['Content-Type'] = 'application/json';
  }

  return headers;
};

const getDefaultFetch =
  (signal: any) =>
  async (...fetchArgs: any[]) => {
    const [url, options] = fetchArgs;
    if (signal) {
      options.signal = signal;
    }
    const res = await window.fetch(url, options);

    if (res && res.status === 401) {
      // Clear all auth state and redirect to MSAL login
      const currentPath = `${window.location.pathname}${window.location.search}`;
      const encoded = encodeURIComponent(currentPath);
      handleUnauthorized(encoded);
      return res;
    }

    return res;
  };

export interface IAPIAction<Arguments extends any[], S, P, M> {
  (...args: Arguments): RSAAAction<S, P, M>;
  type: string;
  typeAPIRequest: string;
  typeAPISuccess: string;
  typeAPIFail: string;
}

interface IRSAACallCustom extends RSAACall {
  data: Record<string, any> | FormData;
  params: any;
  headers?: Record<string, string>;
}

export interface ISetupAction<T extends any[]> {
  (...args: T): Partial<IRSAACallCustom>;
}

const createAPIAction = <Arguments extends any[], S = any, P = any, M = any>(
  type: string,
  setupAction: ISetupAction<Arguments>
): IAPIAction<Arguments, S, P, M> => {
  const actionCreator = (...args: Arguments) => {
    const { endpoint, method, data, fetch, params, headers: customHeaders } = setupAction(...args);

    const signal = get(data, 'signal', null);

    const state = getStore().getState();
    const accessToken = selectAccessTokenFromState(state);
    const defaultFetch = getDefaultFetch(signal);
    const endpointBaseUrl =
      type.indexOf('DOWNLOAD_') > -1 || type.indexOf('UPLOAD_') > -1
        ? ''
        : baseUrl;

    const action: RSAAAction<S, P, M> = {
      [RSAA]: {
        endpoint: `${endpointBaseUrl}${endpoint}${convertParamsToUrl(params)}`,
        method: method || 'GET',
        types: getActionTypes(type),
        headers: {
          ...getAccessHeader(accessToken, data instanceof window.FormData),
          ...customHeaders,
        },
        fetch: defaultFetch,
      },
    };

    if (fetch) {
      action[RSAA].fetch = fetch;
    }

    if (data) {
      if (data instanceof FormData) {
        action[RSAA].body = data;
      } else {
        const { signal, ...requestData } = data; // eslint-disable-line
        action[RSAA].body = JSON.stringify(requestData);
      }
    }

    return action;
  };

  actionCreator.type = type;
  actionCreator.typeAPIRequest = `${type}/REQUEST`;
  actionCreator.typeAPISuccess = `${type}/SUCCESS`;
  actionCreator.typeAPIFail = `${type}/FAIL`;
  return actionCreator;
};

export default createAPIAction;
